/* eslint-disable react-hooks/exhaustive-deps */
import { AutosuggestInput } from '@m/alchemy-ui/AutosuggestInput';
import { Field } from '@m/alchemy-ui/Field';
import { Toast } from '@m/alchemy-ui/Toast';
import { type ToastMessageProps } from '@m/alchemy-ui/ToastMessage';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { type SearchInputProps } from '../../features';
import { getErrorToastMessage } from '../../helpers';

interface Option {
  label: string;
  value: string;
}

const SearchInputStyled = styled(AutosuggestInput)`
  [data-testid='Input'] {
    display: block;
  }

  input {
    padding: 7px 12px;
  }
`;

export const SearchInput = ({ label, endpoint, getExcludeQuery, onSelected }: SearchInputProps) => {
  const minInputLength = 3;
  const intl = useIntl();
  const [value, setValue] = useState<string>('');
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<ToastMessageProps[]>([]);

  useEffect(() => {
    if (value.length >= minInputLength) {
      search(value);
    }
  }, [value]);

  const search = async (query: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${endpoint}${encodeURIComponent(query)}${getExcludeQuery ? getExcludeQuery() : ''}`
      );
      const data = await response.json();

      const enrichedOptions = (data.result as Option[]).map((option) => ({
        ...option,
        onSelect: () => {
          if (onSelected) {
            setValue('');
            onSelected(option);
          }
        }
      }));

      setOptions(enrichedOptions);
    } catch (error) {
      setToastMessage(
        getErrorToastMessage(intl.formatMessage({ id: 'An error occurred', defaultMessage: 'An error occurred' }))
      );
      console.error('An error occurred', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Field
        label={label}
        input={
          <SearchInputStyled
            onKeyUp={(event) => {
              setValue((event.target as HTMLInputElement).value);
            }}
            onChange={(_, option) => {
              if (option?.onSelect) {
                option.onSelect();
              }
            }}
            options={options}
            isLoading={loading}
            value={value}
          />
        }
      />
      <Toast messages={toastMessage} />
    </>
  );
};
